import React, { useEffect, useMemo, useState, useCallback, useRef, FC } from 'react';
import { useHistory } from 'react-router';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import _ from 'lodash';
import clsx from 'clsx';

import FixedLoader from '../../../components/FixedLoader/FixedLoader';
import Api from '../../../api';
import useActionApi from '../../../hooks/useActionApi';
import { usePersistedState } from '../../../hooks';
import entities from '../../../reduxStore/entityKeys';
import { HygienistProps, JobProps } from 'utils/types';
import { PARTIAL_OFFER_STATUS, COUNTRY } from 'utils/constants';
import { getTalentParkingLabel } from 'utils/parking';
import { getMembershipRestrictionDialogCopy } from '../../../utils/membership';
import { JOB_STATUSES } from '../ShiftDetailPage/ShiftDetailPage';

// Components
import Popup from 'components/Popup';
import { MembershipRestrictionModal, type MembershipDialogCopy } from 'components/Modal';
import { ShiftPopup } from '../ShiftDetailPage/components';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { OfferTimer } from 'components/OfferTimer';
import { convertDentalOfficeStatus } from '../ShiftDetailPage/components';
import { getPaymentTerms, getPaymentMethod, getPaymentType } from '../../../utils';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import TimeToLeaveOutlinedIcon from '@mui/icons-material/TimeToLeaveOutlined';
import DiningOutlinedIcon from '@mui/icons-material/DiningOutlined';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import TimelapseOutlinedIcon from '@mui/icons-material/TimelapseOutlined';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import BlurLinearOutlinedIcon from '@mui/icons-material/BlurLinearOutlined';
import LineAxisOutlinedIcon from '@mui/icons-material/LineAxisOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import heartFilled from '../../../images/heartFilled.svg';
import heartUnfilled from '../../../images/heartUnfilled.svg';

import TalentNavbar from '../../../components/TalentNavbar';
import TalentPageTitle from '../../../components/TalentNavbar/TalentPageTitle';
import styles from './PartialOfferPage.module.scss';

dayjs.extend(isToday);

export function navigatePartialOfferBackHome(history: { push: (path: string) => void }, tempStars = (window as any).TempStars) {
    history.push('loading');
    tempStars?.App?.goReact?.('hygienistHome');
}

export function getPartialOfferResubmitErrorState(err: unknown) {
    return {
        membershipDialog: getMembershipRestrictionDialogCopy(err),
    };
}

// Job statuses where a talent can still resubmit an expired offer — mirrors the
// API expiry cron's RESUBMITTABLE_JOB_STATUSES (tempstars-api/src/cron/shifts.js).
const RESUBMITTABLE_JOB_STATUSES: number[] = [
    JOB_STATUSES.POSTED,
    JOB_STATUSES.PARTIAL,
    JOB_STATUSES.OFFER_EXPIRED,
];

// T2-6473: a talent's offer page must key "expired" off the talent's OWN offer
// (PartialOffer.status === EXPIRED), not the aggregate Job.status. Job.status
// only flips to OFFER_EXPIRED when no POSTED offers remain, so it stays PARTIAL
// while another talent still has an active offer — leaving this talent stuck on
// "Offer Pending" with no Resubmit button even though their offer expired.
export function deriveExpiredOfferState(params: {
    hasExpiredOffer: boolean;
    jobStatus?: number | null;
}): { isOfferExpired: boolean; canResubmit: boolean } {
    const isOfferExpired = !!params.hasExpiredOffer;
    const canResubmit =
        isOfferExpired && RESUBMITTABLE_JOB_STATUSES.includes(Number(params.jobStatus));
    return { isOfferExpired, canResubmit };
}

// ============ MAIN COMPONENT ============

const PartialOfferPage: React.FC = () => {
    const history = useHistory();
    
    // ===== PAGE STATE =====
    const [trackData, setTrackData] = useState<any>(null);
    const [isFavouriteDentist, setFavouriteDentist] = useState<boolean>(false);
    const [updatingFavourite, setUpdatingFavourite] = useState<boolean>(false);
    const [isOpenRemoveOfferModal, setIsOpenRemoveOfferModal] = useState<boolean>(false);
    const [isOpenResubmitModal, setIsOpenResubmitModal] = useState<boolean>(false);
    const [membershipDialog, setMembershipDialog] = useState<MembershipDialogCopy | null>(null);
    const [hireJobsCount, setHireJobsCount] = useState<number>(0);

    const locationState = useMemo(
        () => history.location.state as any,
        [history.location.state]
    );

    const { jobId, job: jobData } = locationState || {};
    const [job, setJob] = useState<JobProps>(jobData || ({} as any));

    const [hygienist] = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    });

    // Refs to prevent infinite loops
    const hasFetchedFavourite = useRef<number | null>(null);
    
    // ===== OFFER LOGIC =====
    const offer = useMemo(() => {
        if (!_.isEmpty(job) && hygienist?.id) {
            const foundOffer = _.findLast(job?.partialOffers, (offer: any) => offer.hygienistId === hygienist.id);
            if (foundOffer) {
                // Ensure expiresAt is set - offers typically expire 18 hours after creation
                if (!foundOffer.expiresAt && foundOffer.createdOn) {
                    const createdDate = new Date(foundOffer.createdOn);
                    const expiresDate = new Date(createdDate.getTime() + 18 * 60 * 60 * 1000); // 18 hours in ms
                    return { ...foundOffer, expiresAt: expiresDate.toISOString() };
                }
                return foundOffer;
            }
        }
        return undefined;
    }, [job, hygienist?.id]);

    const isPartialOffer = useMemo(() => {
        if (!job?.shifts?.[0] || !offer) {
            return false;
        }

        const offerStart = offer.offeredStartTimeUTC || offer.offeredStartTime;
        const offerEnd = offer.offeredEndTimeUTC || offer.offeredEndTime;
        const shiftStart = job.shifts[0].postedStart;
        const shiftEnd = job.shifts[0].postedEnd;

        return (shiftStart !== offerStart || shiftEnd !== offerEnd);
    }, [job, offer]);

    const expiredOfferId = useMemo(() => {
        return job?.partialOffers?.find((offer: any) => offer.hygienistId === hygienist?.id
            && offer.status === PARTIAL_OFFER_STATUS.EXPIRED)?.id;
    }, [job, hygienist?.id]);

    // ===== API CALLS =====
    const handleJobDetailsSuccess = useCallback((jobObj: any) => {
        if (_.isEmpty(jobObj)) return;
        setJob(jobObj);
    }, []);

    const handleFavouriteSuccess = useCallback((data: any) => {
        setFavouriteDentist(data?.isFavouriteDentist ?? false);
    }, []);

    const [getJobDetailsLoading, , getJobDetails] = useActionApi({
        apiMethod: Api.getJobDetails,
        onSuccess: handleJobDetailsSuccess
    });

    const [removePartialOfferLoading, , removePartialOffer] = useActionApi({
        apiMethod: Api.removePartialOffer,
        onSuccess: () => {
            // T2-6024: After removing the (expired) offer, the talent's
            // calendar + available-shifts state on `hygienistHome` is stale —
            // the React MemoryRouter keeps the home view mounted, so a plain
            // `history.push('hygienistHome')` does NOT re-run the
            // `useHygienistCalendarData` fetch and the shift looks like it's
            // still gone. Mirror the `resubmitOffer.onSuccess` pattern
            // (loading shim + goReact) which forces a fresh mount of the
            // legacy F7 shell, which in turn re-mounts the React home and
            // re-fetches getAvailableJobs / getHygienistOffers.
            navigatePartialOfferBackHome(history);
        },
    });

    const [checkFavouriteLocationLoading, , checkFavouriteLocation] = useActionApi({
        apiMethod: Api.checkFavouriteLocation,
        onSuccess: handleFavouriteSuccess
    });

    const [resubmitOfferLoading, , resubmitOffer] = useActionApi({
        apiMethod: Api.resubmitOffer,
        onSuccess: () => {
            if (trackData) {
                (window as any).TempStars?.Analytics?.track('resubmitted shift', trackData);
            }
            navigatePartialOfferBackHome(history);
        },
        onError: (err) => {
            const errorState = getPartialOfferResubmitErrorState(err);
            if (errorState.membershipDialog) {
                setMembershipDialog(errorState.membershipDialog);
            }
        }
    });

    // ===== COMPUTED VALUES =====
    const shouldFetchJob = useMemo(() => _.isEmpty(jobData) && jobId, [jobData, jobId]);
    const isJobEmpty = useMemo(() => _.isEmpty(job), [job]);
    const hasValidShifts = useMemo(() => job?.shifts && job.shifts.length > 0, [job?.shifts]);

    const formattedPostedDate = useMemo(() => {
        return job.postedOn ? dayjs(job.postedOn).format('dddd, MMM D') : '';
    }, [job.postedOn]);

    const formattedShiftDate = useMemo(() => {
        return job.shifts?.[0]?.shiftDate ? dayjs(job.shifts[0].shiftDate).format('dddd, MMM D') : '';
    }, [job.shifts]);

    const shiftTime = useMemo(() => {
        if (!job.shifts?.[0]) return '';
        return `${job.shifts[0].postedStart} - ${job.shifts[0].postedEnd}`;
    }, [job.shifts]);

    const avgRecall = useMemo(() => job.location?.avgApptTime || '30 min', [job.location?.avgApptTime]);

    const hourlyRate = useMemo(() => offer?.hourlyRate, [offer?.hourlyRate]);

    const proposedRateForRange = useMemo(() => {
        return job.proposedRate ?? job.avgRate ?? job.hourlyRate;
    }, [job.proposedRate, job.avgRate, job.hourlyRate]);

    const basePayRateRange = useMemo(() => {
        const rate = proposedRateForRange;
        if (rate === undefined || rate === null) return '';
        const n = Number(rate);
        if (Number.isNaN(n)) return '';
        const minRate = Math.max(n - 5, 30);
        return `$${minRate} - $${n}`;
    }, [proposedRateForRange]);

    const paymentDescription = useMemo(() => {
        const terms = getPaymentTerms(job.paymentTerms);
        const method = job.paymentMethod
            ? `typically via ${getPaymentMethod(job.paymentMethod, job.location?.country)}`
            : '';
        return `Office Pays: ${terms} ${method}`;
    }, [job.paymentTerms, job.paymentMethod, job.location?.country]);

    const safetyGuidelinesText = useMemo(() => {
        return `This office has confirmed they meet or exceed the ${job.location?.province || ''} guidelines for workplace safety and infection control.`;
    }, [job.location?.province]);

    const hasEndorsements = useMemo(() => {
        return !!job.location?.ppeEndorsed || !!job.location?.friendlyEndorsed || !!job.location?.equipEndorsed;
    }, [job.location]);

    // Track data for analytics
    const memoizedTrackData = useMemo(() => {
        if (_.isEmpty(job) || !hasValidShifts) return null;
        return {
            isInstabookEnabled: !!job.Instabook,
            recommendedHourlyRate: job.proposedRate,
            currencyCode: job.location?.country ? 'USD' : 'CAD',
            dentalOfficeLocationId: job.locationId,
            dentalOfficeId: job.dentistId,
            dentalOfficeStatus: convertDentalOfficeStatus(job.location?.status ?? 0),
            shiftStartDate: new Date(
                job.startDate?.replace(/-/g, '/') + ' ' + job.shifts[0].postedStart
            ).toISOString(),
            shiftEndDate: new Date(
                job.startDate?.replace(/-/g, '/') + ' ' + job.shifts[0].postedEnd
            ).toISOString(),
            shiftId: job.shifts?.[0]?.id,
            shiftPostDate: job.postedOn ? new Date(job.postedOn).toISOString() : ''
        };
    }, [job, hasValidShifts]);

    // ===== EFFECTS =====
    useEffect(() => {
        if (shouldFetchJob && jobId) {
            getJobDetails(jobId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldFetchJob, jobId]);

    useEffect(() => {
        const locationId = job?.locationId;
        if (locationId && hasFetchedFavourite.current !== locationId) {
            hasFetchedFavourite.current = locationId;
            checkFavouriteLocation(locationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.locationId]);

    useEffect(() => {
        setTrackData(memoizedTrackData);
    }, [memoizedTrackData]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await Api.getAvailableHireJobsCount();
                if (mounted) setHireJobsCount(Number(res?.result ?? 0));
            } catch {}
        })();
        return () => { mounted = false; };
    }, []);

    // ===== HANDLERS =====
    const handleBackNavigation = useCallback(() => {
        history.push({ pathname: 'hygienistHome' });
    }, [history]);

    const handleJobBoardClick = useCallback(() => {
        (window as any).TempStars?.App?.goReact?.('jobPostsList');
    }, []);

    const handleToggleFavourite = async () => {
        if (!job) return;
        setUpdatingFavourite(true);
        try {
            if (isFavouriteDentist) {
                await Api.removeFavouriteDentistByLocationId(job.locationId);
            } else {
                await Api.setFavouriteDentist(job.dentistId, job.locationId);
            }
            checkFavouriteLocation(job.locationId);
        } catch {}
        setUpdatingFavourite(false);
    };

    const handleViewMap = () => {
        const hygienistAddress = hygienist?.lat + ',+' + hygienist?.lon;
        const officeAddress = job.location?.lat + ',+' + job.location?.lon;
        const url = `https://www.google.com/maps/dir/${hygienistAddress}/${officeAddress}`;
        if ((window as any).Cordova) {
            (window as any).cordova.InAppBrowser.open(url, '_system', 'location=yes');
        } else {
            window.open(url, '_blank', 'location=yes');
        }
    };

    const handleRemoveOffer = () => {
        setIsOpenRemoveOfferModal(true);
    };

    const handleConfirmRemoveOffer = () => {
        if (offer?.id) {
            removePartialOffer(offer.id);
        }
    };

    const handleConfirmResubmit = async () => {
        setIsOpenResubmitModal(false);
        if (expiredOfferId) {
            await resubmitOffer(expiredOfferId);
        }
    };

    // Check if offer is expired (T2-6473: offer-centric, not Job.status-centric)
    const { isOfferExpired, canResubmit } = useMemo(
        () => deriveExpiredOfferState({ hasExpiredOffer: !!expiredOfferId, jobStatus: job.status }),
        [job.status, expiredOfferId]
    );

    // ===== LOADING STATE =====
    const isLoading = getJobDetailsLoading || removePartialOfferLoading || checkFavouriteLocationLoading || updatingFavourite || resubmitOfferLoading;

    if (isJobEmpty && !getJobDetailsLoading) {
        return (
            <div className={styles.pageContainer}>
                <FixedLoader loading={getJobDetailsLoading} />
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {/* ===== MODALS ===== */}
            {/* T2-6024: Confirm modal copy adapts to whether the offer is
                still pending ("Remove Offer") or already expired
                ("Remove Expired Offer") so the action label is unambiguous. */}
            <Popup opened={isOpenRemoveOfferModal} onClose={() => setIsOpenRemoveOfferModal(false)}>
                {hasValidShifts && (
                    <ShiftPopup
                        title={isOfferExpired ? 'Remove Expired Offer?' : 'Remove Offer?'}
                        submit={isOfferExpired ? 'Remove Expired Offer' : 'Remove Offer'}
                        close="Cancel"
                        job={job}
                        onClose={() => setIsOpenRemoveOfferModal(false)}
                        onSubmit={handleConfirmRemoveOffer}
                    />
                )}
            </Popup>

            <Popup opened={isOpenResubmitModal} onClose={() => setIsOpenResubmitModal(false)}>
                {hasValidShifts && (
                    <ShiftPopup
                        title="Resubmit offer"
                        submit="Resubmit"
                        close="Cancel"
                        job={job}
                        onClose={() => setIsOpenResubmitModal(false)}
                        onSubmit={handleConfirmResubmit}
                    />
                )}
            </Popup>

            <MembershipRestrictionModal
                isOpen={!!membershipDialog}
                copy={membershipDialog}
                onClose={() => setMembershipDialog(null)}
            />

            <TalentNavbar
                firstName={hygienist?.firstName}
                lastName={hygienist?.lastName}
                photoUrl={hygienist?.photoUrl || undefined}
                isHygOrAsst={hygienist?.type === 0 || hygienist?.type === 1}
                instabookActivated={hygienist?.InstabookActivated === 1}
                isTestAccount={(hygienist as any)?.isTest === 1}
                showAddShiftButton={true}
                showJobBoardButton={true}
                hireJobsCount={hireJobsCount}
                onJobBoardClick={handleJobBoardClick}
                hideAddShiftOnMobile
                pageTitle={isOfferExpired ? 'Expired Custom Offer' : 'Custom Offer'}
            />

            {/* ===== MAIN CONTENT ===== */}
            {!isJobEmpty && (
                <main className={styles.mainContent}>
                    <TalentPageTitle title={isOfferExpired ? 'Expired Custom Offer' : 'Custom Offer'} />
                    {/* ===== SUBHEADER ===== */}
                    <div className={styles.subheader}>
                        <button className={styles.backButton} onClick={handleBackNavigation}>
                            <ArrowBackIcon />
                        </button>
                        <div className={styles.subheaderActions}>
                            {isOfferExpired ? (
                                <div className={styles.offerExpiredTopBadge}>
                                    <AccessTimeOutlinedIcon className={styles.expiredBadgeIcon} />
                                    Offer Expired
                                </div>
                            ) : (
                                <div className={styles.offerPendingBadge}>
                                    <div className={styles.offerPendingDot}></div>
                                    Offer Pending
                                </div>
                            )}
                            <button className={styles.locationButton} onClick={handleViewMap}>
                                <RoomOutlinedIcon />
                                Location
                            </button>
                            <button
                                className={styles.heartButton}
                                onClick={handleToggleFavourite}
                                disabled={updatingFavourite}
                            >
                                <img
                                    src={isFavouriteDentist ? heartFilled : heartUnfilled}
                                    alt={isFavouriteDentist ? 'Remove from favourites' : 'Add to favourites'}
                                    className={styles.heartIcon}
                                />
                            </button>
                            {/* T2-5850/T2-5856: "Withdraw Offer" only makes
                                sense while the offer is still pending. Once
                                it's expired, the primary action is Resubmit
                                (rendered as a full-width button below the
                                rate card), so we hide Withdraw here. */}
                            {hasValidShifts && offer && !isOfferExpired && (
                                <button className={styles.withdrawOfferButton} onClick={handleRemoveOffer}>
                                    Withdraw Offer
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Main Content Box - wraps everything from office name to bottom */}
                    <div className={styles.mainContentBox}>
                        {/* Office Card */}
                        <div className={styles.officeCard}>
                            <div className={styles.officeNameRow}>
                                <div>
                                    <h1 className={styles.officeName}>{job.location?.practiceName}</h1>
                                    <p className={styles.officeLocation}>
                                        {job.location?.city}, {job.location?.province} {job.location?.postalCode}
                                    </p>
                                </div>
                                <div className={styles.offerStatusArea}>
                                    {isOfferExpired ? (
                                        <div className={styles.offerExpiredBadge}>
                                            <AccessTimeOutlinedIcon />
                                            Offer Expired
                                        </div>
                                    ) : (
                                        offer && offer.expiresAt && (
                                            <div className={styles.offerTimerInBox}>
                                                <OfferTimer expiresAt={offer.expiresAt} />
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>

                    {/* Divider above Shift Info */}
                    <hr className={styles.divider} />

                    {/* Shift Info Row - separate boxes */}
                    <div className={styles.shiftInfoRow}>
                        <div className={styles.shiftInfoItem}>
                            <div className={styles.shiftInfoLabel}>
                                <div className={styles.shiftInfoIconCircle}>
                                    <CalendarTodayOutlinedIcon />
                                </div>
                                Shift Date
                            </div>
                            <div className={styles.shiftInfoValue}>{formattedShiftDate}</div>
                        </div>
                        <div className={styles.shiftInfoItem}>
                            <div className={styles.shiftInfoLabel}>
                                <div className={styles.shiftInfoIconCircle}>
                                    <AccessTimeOutlinedIcon />
                                </div>
                                Shift Time
                            </div>
                            <div className={styles.shiftInfoValue}>{shiftTime}</div>
                        </div>
                        <div className={styles.shiftInfoItem}>
                            <div className={styles.shiftInfoLabel}>
                                <div className={styles.shiftInfoIconCircle}>
                                    <TimelapseOutlinedIcon />
                                </div>
                                Avg. Recall
                            </div>
                            <div className={styles.shiftInfoValue}>{avgRecall}</div>
                        </div>
                    </div>

                    {/* Posted On */}
                    <div className={styles.postedOn}>Posted On: {formattedPostedDate}</div>

                    {/* Divider above Offer Rate */}
                    <hr className={styles.divider} />

                    {/* Offer Rate Card */}
                    {hourlyRate && (
                        <div className={styles.offerRateCard}>
                            <div className={styles.offerRateContent}>
                                <div className={styles.offerRateTitle}>
                                    <AttachMoneyOutlinedIcon />
                                    Your Offer Rate
                                </div>
                                <div className={styles.offerRateDescription}>{paymentDescription}</div>
                            </div>
                            <div className={styles.offerRateValue}>${hourlyRate}/hour</div>
                        </div>
                    )}

                    {/* T2-5850: Resubmit Offer CTA sits below the rate card so it
                        visually replaces the "Submit offer at your rate" button from
                        the active-offer view. */}
                    {isOfferExpired && (
                        <>
                            {/* T2-6473: only offer Resubmit while the shift is
                                still open (resubmittable). On a booked/removed/
                                date-expired shift we keep the "Remove Expired
                                Offer" action but hide Resubmit. */}
                            {canResubmit && (
                                <div className={styles.resubmitOfferRow}>
                                    <button
                                        className={styles.resubmitButtonFull}
                                        onClick={() => setIsOpenResubmitModal(true)}
                                    >
                                        <EventAvailableIcon />
                                        Resubmit Offer
                                    </button>
                                </div>
                            )}
                            {/* T2-6024: "Remove Expired Offer" as a secondary
                                action below Resubmit. Clears the expired offer
                                from this shift so the shift becomes available
                                to the talent again. Uses Coral per design guide
                                for destructive actions. */}
                            {offer?.id && (
                                <div className={styles.removeExpiredOfferRow}>
                                    <button
                                        className={styles.removeExpiredOfferButton}
                                        onClick={handleRemoveOffer}
                                        data-testid="remove-expired-offer-button"
                                    >
                                        <DeleteOutlineIcon />
                                        Remove Expired Offer
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* Base Pay Range */}
                    {basePayRateRange && (
                        <div className={styles.basePayRange}>
                            <div className={styles.basePayRangeTitle}>Base Pay Range</div>
                            <div className={styles.basePayRangeValue}>{basePayRateRange}/hr</div>
                            {job.location?.country === COUNTRY.US && typeof job.paysW2 === 'number' && (
                                <div className={styles.basePayRangeSub}>
                                    Working relationship: {getPaymentType(job.paysW2)}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Details Grid */}
                    <div className={styles.detailsGrid}>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><PersonOutlineOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Primary Contact</div>
                                <div className={styles.detailValue}>{job.location?.primaryContact || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><WavesOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Ultrasonic</div>
                                <div className={styles.detailValue}>{job.location?.ultrasonic || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><DesktopMacOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Software</div>
                                <div className={styles.detailValue}>{job.location?.software || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><TimeToLeaveOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Parking</div>
                                <div className={styles.detailValue}>{getTalentParkingLabel(job.location?.parking) || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><TimelapseOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Avg Recall</div>
                                <div className={styles.detailValue}>{job.location?.avgApptTime || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><DiningOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Lunch-break</div>
                                <div className={styles.detailValue}>Unpaid</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><BlurLinearOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Radiography</div>
                                <div className={styles.detailValue}>{job.location?.radiography || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><LineAxisOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Charting</div>
                                <div className={styles.detailValue}>{job.location?.charting || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><SpaOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Scent Policy</div>
                                <div className={`${styles.detailValue} ${job.location?.scentFreePolicy !== 'scent-free' ? styles.warning : ''}`}>
                                    {job.location?.scentFreePolicy === 'scent-free' ? 'Scent-Free' : 'Not Scent-Free'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Endorsements Section */}
                    {hasEndorsements && (
                        <div className={styles.endorsementsSection}>
                            <div className={styles.endorsementsTitle}>
                                Your fellow TempStars members have worked there and say:
                            </div>
                            <div className={styles.endorsementsList}>
                                {job.location?.ppeEndorsed && (
                                    <div className={styles.endorsementItem}>
                                        <div className={`${styles.endorsementIcon} ${styles.ppe}`}>
                                            <SecurityOutlinedIcon />
                                        </div>
                                        <span className={styles.endorsementText}>PPE Endorsed</span>
                                    </div>
                                )}
                                {job.location?.friendlyEndorsed && (
                                    <div className={styles.endorsementItem}>
                                        <div className={`${styles.endorsementIcon} ${styles.friendly}`}>
                                            <SentimentSatisfiedAltOutlinedIcon />
                                        </div>
                                        <span className={styles.endorsementText}>Friendly team</span>
                                    </div>
                                )}
                                {job.location?.equipEndorsed && (
                                    <div className={styles.endorsementItem}>
                                        <div className={`${styles.endorsementIcon} ${styles.equipment}`}>
                                            <LaptopMacOutlinedIcon />
                                        </div>
                                        <span className={styles.endorsementText}>Good equipment</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Safety Guidelines */}
                    <div className={styles.safetyGuidelines}>
                        <CheckCircleOutlineIcon />
                        <span className={styles.safetyText}>{safetyGuidelinesText}</span>
                    </div>
                    </div>
                    {/* End Main Content Box */}
                </main>
            )}

            <FixedLoader loading={isLoading} />
        </div>
    );
};

export default PartialOfferPage;
